/**
 * Public Learning Path Play Page
 * Displays a published A4 learning path and opens clicked element content in an in-page drawer.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Button, Card, Drawer, Empty, Space, Spin, Typography } from 'antd';
import { FilePdfOutlined, FormOutlined, LinkOutlined, PlayCircleOutlined, QuestionCircleOutlined, SoundOutlined, ToolOutlined } from '@ant-design/icons';
import PagesWrapper from '../../PagesWrapper';
import { getFileUrl } from '../../utils/urlUtils';
import {
  getPublicLearningPath,
  recordLearningPathElementInteraction,
  recordLearningPathVisit,
} from '../../services/learningPathsService';
import styles from './LearningPaths.module.css';

const { Title } = Typography;

const ELEMENT_ACTIONS = [
  {
    key: 'pdf',
    field: 'pdf',
    icon: <FilePdfOutlined />,
    label: 'PDF',
    getHref: (element) => `/api/files/${element.pdf.id}/download`,
    getText: (element) => `Open PDF: ${element.pdf.fileName}`,
    interactionType: 'pdf-open',
  },
  {
    key: 'link',
    field: 'linkUrl',
    icon: <LinkOutlined />,
    label: 'Link',
    getHref: (element) => element.linkUrl,
    getText: () => 'Open external link',
    interactionType: 'link-open',
  },
  {
    key: 'quiz',
    field: 'quizCollectionId',
    icon: <QuestionCircleOutlined />,
    label: 'Quiz',
    getHref: (element) => `/quiz/${element.quizCollectionId}`,
    getText: (element) => `Open quiz: ${element.quizCollectionName || element.quizCollectionId}`,
    interactionType: 'quiz-open',
  },
  {
    key: 'survey',
    field: 'surveyId',
    icon: <FormOutlined />,
    label: 'Survey',
    getHref: (element) => `/survey/${element.surveyId}`,
    getText: (element) => `Open survey: ${element.surveyName || element.surveyId}`,
    interactionType: 'survey-open',
  },
  {
    key: 'training-profile',
    field: 'trainingProfileId',
    icon: <ToolOutlined />,
    label: 'Training profile',
    getHref: (element) => `/simulations/heat-pump-training?profileId=${element.trainingProfileId}`,
    getText: (element) => `Open training profile: ${element.trainingProfileName || element.trainingProfileId}`,
    interactionType: 'training-profile-open',
  },
];

/**
 * Public learner-facing Learning Path playback page.
 * @returns {JSX.Element} Learning Path viewer
 */
const LearningPathPlay = () => {
  const { id } = useParams();
  const [path, setPath] = useState(null);
  const [selectedElement, setSelectedElement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(1);

  const loadPath = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getPublicLearningPath(id);
      setPath(result);
      recordLearningPathVisit(id).catch(() => {});
    } catch (err) {
      setError('Learning path not found or not published.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadPath();
  }, [loadPath]);

  const openElement = (element) => {
    setSelectedElement(element);
    recordLearningPathElementInteraction(id, element.id, 'element-click').catch(() => {});
  };

  const recordOpen = (element, interactionType) => {
    recordLearningPathElementInteraction(id, element.id, interactionType).catch(() => {});
  };

  if (loading) {
    return (
      <PagesWrapper>
        <div className={styles.playShell}><Spin /></div>
      </PagesWrapper>
    );
  }

  if (error) {
    return (
      <PagesWrapper>
        <div className={styles.playShell}><Alert type="error" message={error} /></div>
      </PagesWrapper>
    );
  }

  return (
    <PagesWrapper>
      <div className={styles.playShell}>
        <div className={styles.pageHeader}>
          <div>
            <h1 className={styles.pageTitle}>{path.name}</h1>
            <p className={styles.pageDescription}>Click any hotspot on the image to open its linked content.</p>
          </div>
          <div className={styles.playToolbar}>
            <span className={styles.playZoomValue}>{Math.round(zoom * 100)}%</span>
            <Space>
              <Button onClick={() => setZoom((value) => Math.max(0.5, value - 0.1))}>-</Button>
              <Button onClick={() => setZoom((value) => Math.min(2, value + 0.1))}>+</Button>
              <Button onClick={() => setZoom(1)}>Reset</Button>
            </Space>
          </div>
        </div>

        <div className={styles.playCanvasShell}>
          <div className={`${styles.a4Page} ${styles.playA4Page}`} style={{ transform: `scale(${zoom})`, marginBottom: `${(zoom - 1) * 900}px` }}>
            {path.backgroundImage ? (
              <img className={styles.backgroundImage} src={getFileUrl(path.backgroundImage.relativeUrl)} alt={path.backgroundImage.altText || path.name} />
            ) : (
              <div className={styles.backgroundPlaceholder}>No background image available.</div>
            )}
            {(path.elements || []).map((element) => (
              <button
                key={element.id}
                type="button"
                className={`${styles.pathElement} ${styles.playPathElement}`}
                style={{
                  left: `${element.xPercent}%`,
                  top: `${element.yPercent}%`,
                  width: `${element.widthPercent}%`,
                  height: `${element.heightPercent}%`,
                }}
                onClick={() => openElement(element)}
                aria-label={`Open ${element.name}`}
                title={element.name}
              />
            ))}
          </div>
        </div>
      </div>

      <Drawer
        title={selectedElement?.name}
        open={Boolean(selectedElement)}
        onClose={() => setSelectedElement(null)}
        width={620}
      >
        {selectedElement ? (
          <div className={styles.playDrawerContent}>
            <div className={styles.playDrawerHeader}>
              <div className={styles.playDrawerTitle}>{selectedElement.name}</div>
              <div className={styles.playDrawerHint}>Open the resources linked to this hotspot.</div>
            </div>

            {selectedElement.richTextContentInfo && (
              <div className={styles.richContent} dangerouslySetInnerHTML={{ __html: selectedElement.richTextContentInfo }} />
            )}

            {selectedElement.audio && (
              <div className={styles.playInlineSection}>
                <div className={styles.playInlineSectionLabel}>
                  <span className={styles.playInlineSectionIcon}><SoundOutlined /></span>
                  <span>Audio</span>
                </div>
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <audio controls src={`/api/files/${selectedElement.audio.id}/download`} onPlay={() => recordOpen(selectedElement, 'audio-open')} style={{ width: '100%' }} />
              </div>
            )}

            {selectedElement.video && (
              <Card size="small" title={<span className={styles.playVideoTitle}><PlayCircleOutlined /> Video</span>}>
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video controls src={`/api/files/${selectedElement.video.id}/download`} onPlay={() => recordOpen(selectedElement, 'video-open')} style={{ width: '100%' }} />
              </Card>
            )}

            {ELEMENT_ACTIONS.filter((action) => Boolean(selectedElement[action.field])).length > 0 && (
              <div className={styles.playActionList}>
                {ELEMENT_ACTIONS.filter((action) => Boolean(selectedElement[action.field])).map((action) => (
                  <a
                    key={action.key}
                    className={styles.playActionLink}
                    href={action.getHref(selectedElement)}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => recordOpen(selectedElement, action.interactionType)}
                  >
                    <span className={styles.playActionIcon}>{action.icon}</span>
                    <span className={styles.playActionBody}>
                      <span className={styles.playActionLabel}>{action.label}</span>
                      <span className={styles.playActionText}>{action.getText(selectedElement)}</span>
                    </span>
                  </a>
                ))}
              </div>
            )}

            {!selectedElement.richTextContentInfo && !selectedElement.pdf && !selectedElement.audio && !selectedElement.video && !selectedElement.linkUrl && !selectedElement.quizCollectionId && !selectedElement.surveyId && !selectedElement.trainingProfileId && (
              <Empty description="No content configured for this element yet." />
            )}
          </div>
        ) : null}
      </Drawer>
    </PagesWrapper>
  );
};

export default LearningPathPlay;
